import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  AlertTriangle,
  Bot,
  User,
  ChevronDown,
  ChevronUp,
  BarChart3,
} from 'lucide-react'
import {
  getClosureStats,
  type ClosureStatsResponse,
  type ClosedConversationExportItem,
} from '@/services/dashboardApi'
import { useVerticalUI } from '@/lib/verticalUI/useVerticalUI'
import { useAuthStore } from '@/store/authStore'
import { queryKeys } from '@/lib/queryKeys'

export function ClosureStatsWidget() {
  const [days, setDays] = useState<number>(30)
  const [showDetails, setShowDetails] = useState<boolean>(false)
  const { profile } = useVerticalUI()
  const tenantId = useAuthStore((s) => s.tenantId)
  const isFulfillment = profile.operational.hasFulfillment

  const { data, isLoading, error } = useQuery<ClosureStatsResponse>({
    queryKey: queryKeys.closureStats(tenantId, days),
    queryFn: () => getClosureStats({ days }),
    staleTime: 60_000,
  })

  const handleExportCsv = () => {
    if (!data?.items?.length) return

    const headers = [
      'ID Conversación',
      'Contacto',
      'Teléfono',
      'Resultado',
      'Motivo',
      'Notas',
      'Fecha Cierre',
      'Asesor / Canal',
      isFulfillment ? 'Monto Orden ($)' : 'Monto Estimado ($)',
    ]

    const rows = data.items.map((item: ClosedConversationExportItem) => [
      item.id,
      `"${(item.contactName || '').replace(/"/g, '""')}"`,
      `"${item.waContact}"`,
      item.category === 'won'
        ? (isFulfillment ? 'Venta Exitosa' : `${profile.terminology.primaryTransactionSingular} Concretada`)
        : 'No Concretada',
      `"${item.closeReasonLabel}"`,
      `"${(item.closeNotes || '').replace(/"/g, '""')}"`,
      item.closedAt,
      `"${item.agentName}"`,
      item.orderTotal.toFixed(2),
    ])

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `reporte-cierres-${days}d-${new Date().toISOString().slice(0, 10)}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const summary = data?.summary
  const winRate = summary?.winRate ?? 0
  const winRateColor =
    winRate >= 60
      ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
      : winRate >= 35
        ? 'text-amber-700 bg-amber-50 border-amber-200'
        : 'text-rose-700 bg-rose-50 border-rose-200'

  const topLossReason = data?.byReason?.find((r) => r.category === 'lost')

  return (
    <div
      role="region"
      aria-label={isFulfillment ? 'Estadísticas de Cierres de Ventas' : `Estadísticas de ${profile.terminology.primaryTransactionPlural}`}
      className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs"
    >
      {/* Header con Título y Filtros */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="rounded-xl bg-emerald-50 p-2 text-emerald-700 border border-emerald-100">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm sm:text-base font-bold text-slate-900">
              {isFulfillment
                ? 'Efectividad de Cierres de Ventas'
                : `Efectividad de ${profile.terminology.primaryTransactionPlural}`}
            </h2>
            <p className="text-xs text-slate-500">
              {isFulfillment
                ? 'Tasa de conversión, diagnóstico de pérdidas y tiempo de cierre'
                : 'Tasa de concreción, cancelaciones y tiempo de atención'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Selector de Rango */}
          <div className="inline-flex rounded-xl bg-slate-100 p-1 text-xs font-semibold text-slate-600">
            <button
              type="button"
              onClick={() => setDays(7)}
              className={`rounded-lg px-2.5 py-1 transition-all ${
                days === 7 ? 'bg-white text-slate-900 shadow-2xs' : 'hover:text-slate-900'
              }`}
            >
              7d
            </button>
            <button
              type="button"
              onClick={() => setDays(30)}
              className={`rounded-lg px-2.5 py-1 transition-all ${
                days === 30 ? 'bg-white text-slate-900 shadow-2xs' : 'hover:text-slate-900'
              }`}
            >
              30d
            </button>
            <button
              type="button"
              onClick={() => setDays(90)}
              className={`rounded-lg px-2.5 py-1 transition-all ${
                days === 90 ? 'bg-white text-slate-900 shadow-2xs' : 'hover:text-slate-900'
              }`}
            >
              90d
            </button>
          </div>

          {/* Botón Exportar CSV */}
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={!data?.items?.length}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Exportar listado para remarketing"
          >
            <Download className="h-3.5 w-3.5 text-slate-500" />
            <span className="hidden sm:inline">Exportar CSV</span>
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
        </div>
      )}

      {error && !isLoading && (
        <div className="my-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-xs text-rose-700">
          No se pudieron cargar las estadísticas de cierre.
        </div>
      )}

      {!isLoading && !error && summary && (
        <div className="mt-4 space-y-4">
          {/* Grid de 4 KPIs Principales */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* KPI 1: Win Rate */}
            <div className={`rounded-xl border p-3.5 ${winRateColor}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  {isFulfillment ? 'Win Rate (Conversión)' : 'Tasa de Concreción'}
                </span>
                <TrendingUp className="h-4 w-4" />
              </div>
              <p className="mt-1 text-2xl font-black">{winRate}%</p>
              <p className="text-[11px] opacity-80 mt-0.5">
                {summary.totalWon} de {summary.totalClosed} {isFulfillment ? 'cerradas' : 'atendidas'}
              </p>
            </div>

            {/* KPI 2: Ventas Ganadas / Concretadas */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5">
              <div className="flex items-center justify-between text-slate-500">
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  {isFulfillment ? 'Ventas Ganadas' : `${profile.terminology.primaryTransactionPlural} Concretadas`}
                </span>
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="mt-1 text-2xl font-black text-slate-900">
                {summary.totalWon}
              </p>
              <p className="text-[11px] text-emerald-700 font-semibold mt-0.5">
                ${summary.totalRevenue.toFixed(2)} USD {isFulfillment ? 'generados' : 'estimados'}
              </p>
            </div>

            {/* KPI 3: No Concretadas */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5">
              <div className="flex items-center justify-between text-slate-500">
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  {isFulfillment ? 'No Concretadas' : `${profile.terminology.primaryTransactionPlural} No Concretadas`}
                </span>
                <XCircle className="h-4 w-4 text-rose-500" />
              </div>
              <p className="mt-1 text-2xl font-black text-slate-900">
                {summary.totalLost}
              </p>
              <p className="text-[11px] text-slate-500 truncate mt-0.5" title={topLossReason ? `${topLossReason.percentage}% por ${topLossReason.label}` : 'Sin pérdidas'}>
                {topLossReason
                  ? `Top: ${topLossReason.label}`
                  : (isFulfillment ? 'Sin pérdidas registradas' : 'Sin cancelaciones')}
              </p>
            </div>

            {/* KPI 4: Tiempo a Cierre */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5">
              <div className="flex items-center justify-between text-slate-500">
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  {isFulfillment ? 'Tiempo a Cierre' : 'Tiempo de Atención'}
                </span>
                <Clock className="h-4 w-4 text-blue-600" />
              </div>
              <p className="mt-1 text-2xl font-black text-slate-900">
                {summary.avgTimeToCloseHours}h
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Promedio desde primer mensaje
              </p>
            </div>
          </div>

          {/* Toggle de detalles */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowDetails((prev) => !prev)}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors"
            >
              <span>{showDetails ? 'Ocultar desglose detallado' : 'Ver desglose por motivo y asesores'}</span>
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Sección de Desglose Expandible */}
          {showDetails && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
              {/* Columna Izquierda: Motivos de Cierre */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                  Distribución por Motivo de Cierre
                </h3>
                {data.byReason.length === 0 ? (
                  <p className="text-xs text-slate-400 py-3">No hay cierres registrados en este período.</p>
                ) : (
                  <div className="space-y-2">
                    {data.byReason.map((item) => (
                      <div key={item.reason} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-slate-700 flex items-center gap-1.5">
                            {item.category === 'won' ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            ) : (
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                            )}
                            <span className="truncate">{item.label}</span>
                          </span>
                          <span className="font-bold text-slate-900 shrink-0">
                            {item.count} ({item.percentage}%)
                          </span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                          <div
                            className={`h-full rounded-full ${
                              item.category === 'won' ? 'bg-emerald-500' : 'bg-rose-400'
                            }`}
                            style={{ width: `${Math.min(100, item.percentage)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Columna Derecha: Rendimiento por Asesor */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                  Rendimiento por Asesor y Bot
                </h3>
                {data.byAgent.length === 0 ? (
                  <p className="text-xs text-slate-400 py-3">No hay agentes con cierres registrados.</p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {data.byAgent.map((agent) => (
                      <div
                        key={agent.userId}
                        className="flex items-center justify-between rounded-lg bg-white p-2.5 border border-slate-200/60 shadow-2xs text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <div className="rounded-md bg-slate-100 p-1 text-slate-600">
                            {agent.userId === 'bot' ? (
                              <Bot className="h-3.5 w-3.5 text-blue-600" />
                            ) : (
                              <User className="h-3.5 w-3.5 text-emerald-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 truncate max-w-[140px]">
                              {agent.name}
                            </p>
                            <p className="text-[11px] text-slate-400">
                              {agent.totalClosed} cierres ({agent.won} won / {agent.lost} lost)
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
                              agent.winRate >= 50
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {agent.winRate}% Win
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
